import { useState } from "react";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { theme } from "../../../theme";
import { useTranslation } from "react-i18next";
import Modal from "../Modal";
import Body from "../../atoms/typography/Body";
import { useBulkDeleteInvoices } from "../../../graphql/hooks/invoices/useBulkDeleteInvoices";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Alert from "../Alert";
import BodySmall from "../../atoms/typography/BodySmall";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import { Row } from "react-grid-system";
import ActionButton from "../../atoms/buttons/ActionButton";
import { useNavigate } from "react-router-dom";
import CircleButton from "../../atoms/buttons/CircleButton";
import { HighligtActionButton } from "../../atoms/SlideActionPanel";

interface Props {
  buttonType?:
    | "outlined"
    | "outlinedSmallDiscrete"
    | "circle"
    | "highlightActionButton";
  invoiceIds: string[];
  renderTrigger?: (onClick: () => void) => React.ReactNode;
  disabled?: boolean;
}

const DeleteInvoicesButton: React.FC<Props> = ({
  buttonType,
  invoiceIds,
  renderTrigger,
  disabled,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<boolean>(false);
  const [failedDeletions, setFailedDeletions] = useState<
    Array<{ invoiceId: string }>
  >([]);

  const onCompletedDeleteInvoice = (result: any) => {
    const failedDeletions = result?.invoice?.bulkDeleteInvoices.filter(
      (res: any) => !res.success,
    );
    if (failedDeletions.length > 0) {
      setFailedDeletions(failedDeletions);
    } else {
      setSuccessMessage(true);
    }
  };
  const onErrorDeleteInvoice = (e: any) => {
    setErrorMessage(e?.toString() || t("invoices.deleteError"));
  };
  const { bulkDeleteInvoices, error, loading } = useBulkDeleteInvoices({
    onCompleted: onCompletedDeleteInvoice,
    onError: onErrorDeleteInvoice,
  });

  const onClickDelete = async () => {
    try {
      await bulkDeleteInvoices({
        variables: {
          input: {
            invoiceIds: invoiceIds,
          },
        },
      });
    } catch (e) {
      console.error("Error deleting invoices:", e);
      setErrorMessage(e?.toString() || t("invoices.deleteError"));
    }
  };

  return (
    <div>
      {renderTrigger ? (
        renderTrigger(() => setModalOpen(true))
      ) : buttonType === "outlined" ? (
        <OutlinedButton
          color={theme.colors.error}
          onClick={() => setModalOpen(true)}
          title={
            invoiceIds.length > 1
              ? t("invoices.deleteInvoices")
              : t("invoices.deleteInvoice")
          }
          icon={<TrashIcon />}
          medium
        />
      ) : buttonType === "outlinedSmallDiscrete" ? (
        <OutlinedButton
          color={theme.colors.text}
          onClick={() => setModalOpen(true)}
          title={
            invoiceIds.length > 1
              ? t("invoices.deleteInvoices")
              : t("invoices.deleteInvoice")
          }
          icon={<TrashIcon />}
        />
      ) : buttonType === "circle" ? (
        <CircleButton
          color={theme.colors.error}
          onClick={() => setModalOpen(true)}
          icon={<TrashIcon color={theme.colors.error} />}
        />
      ) : buttonType === "highlightActionButton" ? (
        <HighligtActionButton
          color={theme.colors.error}
          onClick={() => setModalOpen(true)}
          disabled={disabled}
        >
          <TrashIcon />
          <BodySmall>{t("invoices.deleteInvoices")}</BodySmall>
        </HighligtActionButton>
      ) : null}
      {modalOpen && (
        <Modal
          title={
            invoiceIds.length > 1
              ? t("invoices.deleteInvoices")
              : t("invoices.deleteInvoice")
          }
          closeIcon
          width={500}
          onClose={() => setModalOpen(false)}
          actionButtons={{
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setModalOpen(false),
            },
            deleteButton: {
              buttonTitle: t("common.delete"),
              onClick: onClickDelete,
              loading: loading,
              disabled: successMessage === true,
            },
          }}
        >
          <Body>{t("invoices.areYouSureYouWantToDeleteTheInvoice")}</Body>
          <Body>
            {t("invoices.youHaveSelectedInvoicesToDelete", {
              count: invoiceIds.length,
            })}
          </Body>

          <HorizontalSpacer spacing={2} />
          {(errorMessage || error) && (
            <Alert
              type="error"
              title={t("invoices.deleteError")}
              message={errorMessage || error?.toString()}
            />
          )}
          {failedDeletions.length > 0 &&
            failedDeletions.map((invoiceId) => (
              <BodySmall
                key={invoiceId.invoiceId}
                color={theme.colors.error}
              >{`Kunde inte radera faktura med id ${invoiceId.invoiceId}`}</BodySmall>
            ))}
          {successMessage && (
            <Alert type="success" title={t("invoices.deleteSuccess")} />
          )}
          {successMessage && buttonType !== "highlightActionButton" && (
            <Row justify="center">
              <ActionButton
                title={t("random.goBack")}
                onClick={() => navigate(-1)}
                color={theme.colors.primary}
              />
            </Row>
          )}
        </Modal>
      )}
    </div>
  );
};

export default DeleteInvoicesButton;
